import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { actionErrorMessage } from "../../../utils/network";
import { useSettingsAccess } from "../SettingsAccessContext";
import {
    ComplianceAPI,
    ClinicDocType,
    ClinicDocument,
    OnboardingComplianceAPI,
    OnboardingComplianceDoc,
    OnboardingDocType,
} from "../../../services/compliance.service";
import { DoctorKycAPI, KycDocument } from "../../../services/doctorKyc.service";
import { SettingsAPI, isDoctorProfile } from "../../../services/settings.service";
import {
    DocTile,
    DocState,
    ACCEPTED_TYPES,
    MAX_FILE_SIZE,
    formatDate,
    toDocStatus,
    putToS3,
    FileIcon,
} from "../../../components/settings/DocTile";

/* ------------------------------------------------------------------ */
/*  Types & constants                                                  */
/* ------------------------------------------------------------------ */

// Where a tile persists:
//   • "clinic" → clinic_documents (institutional compliance docs)
//   • "doctor" → doctor_kyc_documents.medical_registration for the medical
//     director. This is what derive_can_prescribe() reads, so verifying the NMC
//     is exactly what unlocks Start Visit / finalise-prescription for the
//     admin-who-practises (see app/core/authz.py::derive_can_prescribe).
type DocSource = "clinic" | "doctor";

interface CredentialDoc {
    id: string;
    label: string;
    description: string;
    source: DocSource;
    /** Whether this document is required to prescribe / start a visit. Only the
     *  Owner/Director Aadhaar (clinic) and the Medical Director's NMC (doctor)
     *  gate prescribing — see app/core/authz.py. Every other tile is optional. */
    required?: boolean;
    /** clinic_documents.document_type — only for source === "clinic". */
    backendType?: ClinicDocType;
}

// Business/registration docs live in the "Business & registration documents"
// card (ONBOARDING_DOCS); keep only credentialing docs here to avoid duplication.
const DOCS: CredentialDoc[] = [
    { id: "aadhaar", label: "Director / Owner Aadhaar",            description: "Aadhaar card of the clinic director or proprietor",  source: "clinic", backendType: "owner_aadhaar",             required: true },
    { id: "pan",     label: "Director / Owner PAN Card",           description: "PAN card of the clinic director or proprietor",      source: "clinic", backendType: "owner_pan",                required: true },
    { id: "nmc",     label: "Medical Director's NMC Registration", description: "NMC registration certificate of the primary doctor", source: "doctor" },
    { id: "cea",     label: "Clinical Establishment Certificate",  description: "Registration under CEA 2010 or State equivalent",    source: "clinic", backendType: "clinical_establishment_reg" },
];

const DOCTOR_NMC_TYPE = "medical_registration" as const;

// Every tile starts on "pending upload" until the backend load resolves. Derived
// from DOCS so the set stays in sync as tiles are added.
const PENDING_STATE: Record<string, DocState> = Object.fromEntries(
    DOCS.map((d): [string, DocState] => [d.id, { status: "pending" }])
);

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
    </svg>
);

/* ------------------------------------------------------------------ */
/*  Onboarding / business compliance docs                              */
/* ------------------------------------------------------------------ */

const ONBOARDING_DOCS: CredentialDoc[] = [
    { id: "incorporation",      label: "Company Incorporation Certificate",   description: "Certificate of incorporation issued by MCA",           source: "clinic" },
    { id: "gst",                label: "GST Certificate",                     description: "GST registration certificate",                          source: "clinic" },
    { id: "clinic_registration",label: "Clinic Registration Certificate",     description: "State or municipal clinic registration",                source: "clinic" },
    { id: "msme",               label: "MSME / Startup India Certificate",    description: "MSME or Startup India registration (if applicable)",    source: "clinic" },
    { id: "other_regulatory",   label: "Other Regulatory Documents",          description: "Any other required regulatory documentation",           source: "clinic" },
];

const PENDING_OB_STATE: Record<string, DocState> = Object.fromEntries(
    ONBOARDING_DOCS.map(d => [d.id, { status: "pending" as const }])
);

function mapObDoc(doc: OnboardingComplianceDoc): DocState {
    if (doc.status === "not_uploaded") return { status: "pending" };
    return {
        status: doc.status === "verified" ? "verified" : doc.status === "rejected" ? "rejected" : "submitted",
        docId: doc.id ?? undefined,
        rejectionReason: doc.rejection_reason ?? undefined,
        viewUrl: doc.view_url ?? null,
        uploadedAt: formatDate(doc.updated_at),
    };
}

function buildObState(obList: OnboardingComplianceDoc[]): Record<string, DocState> {
    const next: Record<string, DocState> = { ...PENDING_OB_STATE };
    for (const doc of obList) {
        if (PENDING_OB_STATE[doc.doc_type] !== undefined) {
            next[doc.doc_type] = mapObDoc(doc);
        }
    }
    return next;
}

/* ------------------------------------------------------------------ */
/*  State mapping                                                       */
/* ------------------------------------------------------------------ */

function mapClinicDoc(m: ClinicDocument): DocState {
    return {
        status: toDocStatus(m.status),
        uploadedAt: formatDate(m.updated_at ?? m.created_at),
        docId: m.clinic_doc_id,
        rejectionReason: m.rejection_reason ?? undefined,
        viewUrl: m.view_url ?? null,
    };
}

function mapDoctorDoc(m: KycDocument): DocState {
    return {
        status: toDocStatus(m.status),
        uploadedAt: formatDate(m.updated_at ?? m.created_at),
        docId: m.kyc_doc_id,
        rejectionReason: m.rejection_reason ?? undefined,
        viewUrl: null, // doctor-KYC list carries no view_url; fetched on demand
    };
}

function buildState(clinicDocs: ClinicDocument[], kycDocs: KycDocument[]): Record<string, DocState> {
    const next: Record<string, DocState> = Object.fromEntries(
        DOCS.map((d): [string, DocState] => [d.id, { status: "pending" }])
    );
    for (const d of DOCS) {
        if (d.source === "clinic") {
            const m = clinicDocs.find(c => c.document_type === d.backendType);
            if (m) next[d.id] = mapClinicDoc(m);
        } else {
            const m = kycDocs.find(k => k.document_type === DOCTOR_NMC_TYPE);
            if (m) next[d.id] = mapDoctorDoc(m);
        }
    }
    return next;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export const ComplianceVerification: React.FC = () => {
    // View-only grant on Compliance & Verification: uploading and replacing both
    // need settings_compliance.edit server-side, so lock the tiles rather than
    // offer a dropzone that can only 403.
    const { canEdit } = useSettingsAccess();
    const [docs, setDocs] = useState<Record<string, DocState>>(PENDING_STATE);
    const [clinicId, setClinicId] = useState<string | null>(null);
    // The medical director's doctor record. Present when the current user is an
    // admin-who-practises (or a doctor). Required to submit the NMC registration.
    const [doctorId, setDoctorId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [obDocs, setObDocs] = useState<Record<string, DocState>>(PENDING_OB_STATE);
    const [obLoading, setObLoading] = useState(true);

    // Re-fetch both sources and fold them into per-tile state. Called on mount
    // and after every upload so a refresh always reflects what's persisted.
    const refresh = useCallback(async (cId: string | null, dId: string | null) => {
        const [clinicList, kycList] = await Promise.all([
            cId ? ComplianceAPI.listDocuments(cId).catch(() => [] as ClinicDocument[]) : Promise.resolve([] as ClinicDocument[]),
            dId ? DoctorKycAPI.list(dId).catch(() => [] as KycDocument[]) : Promise.resolve([] as KycDocument[]),
        ]);
        setDocs(buildState(clinicList, kycList));
    }, []);

    const refreshOb = useCallback(async (cId: string | null) => {
        if (!cId) { setObDocs(PENDING_OB_STATE); setObLoading(false); return; }
        try {
            const list = await OnboardingComplianceAPI.listDocuments(cId);
            setObDocs(buildObState(list));
        } catch {
            // Non-fatal — tiles stay on "pending upload"
        } finally {
            setObLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const profile = await SettingsAPI.getProfile();
                if (cancelled) return;
                const cId = profile.clinic_id;
                // getProfile only exposes doctor_id once the medical registration is
                // VERIFIED. Fall back to the read-only lookup so a submitted-but-
                // unverified NMC still loads (it's keyed by doctor_id).
                let dId = isDoctorProfile(profile) ? profile.doctor_id : null;
                if (!dId) {
                    dId = (await SettingsAPI.getMyDoctorId().catch(() => ({ doctor_id: null }))).doctor_id;
                }
                if (cancelled) return;
                setClinicId(cId);
                setDoctorId(dId);
                await Promise.all([refresh(cId, dId), refreshOb(cId)]);
            } catch {
                // Non-fatal: tiles stay on "pending upload" if the load fails.
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [refresh, refreshOb]);

    const handleObFileSelect = useCallback(async (id: string, file: File) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error("Only PDF, JPG or PNG files are accepted.");
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error("File size must be under 5 MB.");
            return;
        }
        if (!clinicId) {
            toast.error("Clinic details are still loading — please try again in a moment.");
            return;
        }
        setObDocs(prev => ({ ...prev, [id]: { ...prev[id], status: "submitted", fileName: file.name, uploading: true } }));
        try {
            const { upload_url, s3_key } = await OnboardingComplianceAPI.getPresignedUploadUrl(clinicId, id as OnboardingDocType);
            if (upload_url) await putToS3(upload_url, file);
            const updated = await OnboardingComplianceAPI.confirmUpload(clinicId, id as OnboardingDocType, s3_key);
            setObDocs(prev => ({ ...prev, [id]: { ...mapObDoc(updated), fileName: file.name } }));
            toast.success(`${ONBOARDING_DOCS.find(d => d.id === id)?.label ?? "Document"} uploaded — pending review`);
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to upload document. Please try again."));
            setObDocs(prev => ({ ...prev, [id]: { ...prev[id], uploading: false, fileName: undefined, status: prev[id].docId ? prev[id].status : "pending" } }));
        }
    }, [clinicId]);

    const handleObView = useCallback(async (id: string) => {
        const st = obDocs[id];
        if (st?.viewUrl) {
            window.open(st.viewUrl, "_blank", "noopener,noreferrer");
            return;
        }
        if (!clinicId) return;
        try {
            const url = await OnboardingComplianceAPI.getViewUrl(clinicId, id as OnboardingDocType);
            if (url) window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Could not open the document. Please try again.");
        }
    }, [obDocs, clinicId]);

    const handleFileSelect = useCallback(async (id: string, file: File) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error("Only PDF, JPG or PNG files are accepted.");
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error("File size must be under 5 MB.");
            return;
        }
        const doc = DOCS.find(d => d.id === id);
        if (!doc) return;
        // Replacing an already-verified document re-enters SwasthiQ review. For the
        // medical registration (NMC) that also pauses prescribing — can_prescribe
        // flips back to false until it's verified again — so confirm first.
        if (docs[id]?.status === "verified") {
            const msg = doc.source === "doctor"
                ? "Replace your verified medical registration? The new file is sent for review and prescribing is paused until it's re-verified."
                : `Replace the verified "${doc.label}"? The new file will be sent back to SwasthiQ for review.`;
            if (!window.confirm(msg)) return;
        }
        if (doc.source === "clinic" && !clinicId) {
            toast.error("Clinic details are still loading — please try again in a moment.");
            return;
        }

        setDocs(prev => ({ ...prev, [id]: { ...prev[id], status: "submitted", fileName: file.name, uploading: true } }));
        try {
            if (doc.source === "clinic") {
                const presign = await ComplianceAPI.getPresignedUrls(clinicId!);
                const entry = presign.entries[doc.backendType!];
                if (!entry) throw new Error("No presigned URL for this document type.");
                if (entry.upload_url) await putToS3(entry.upload_url, file);
                await ComplianceAPI.confirmUploads(clinicId!, [
                    { document_type: doc.backendType!, s3_key: entry.s3_key },
                ]);
                await refresh(clinicId, doctorId);
            } else {
                // NMC → the medical director's medical_registration KYC doc. An admin
                // without a doctor record self-enrols first (creates an inactive Doctor
                // row); once PlatformAdmin verifies the doc, verify_document activates
                // the row and derive_can_prescribe() flips to true.
                let dId = doctorId;
                if (!dId) {
                    dId = (await SettingsAPI.enrollAsPractitioner()).doctor_id;
                    setDoctorId(dId);
                }
                const presign = await DoctorKycAPI.presign(dId, [DOCTOR_NMC_TYPE]);
                const entry = presign.entries.find(e => e.document_type === DOCTOR_NMC_TYPE);
                if (!entry) throw new Error("No presigned URL for NMC registration.");
                if (entry.upload_url) await putToS3(entry.upload_url, file);
                await DoctorKycAPI.confirm(dId, [
                    { document_type: DOCTOR_NMC_TYPE, s3_key: entry.s3_key },
                ]);
                await refresh(clinicId, dId);
            }
            toast.success(`${doc.label} uploaded — pending review`);
        } catch (err) {
            toast.error(actionErrorMessage(err, `Failed to upload ${doc.label}. Please try again.`));
            setDocs(prev => ({ ...prev, [id]: { ...prev[id], uploading: false, fileName: undefined, status: prev[id].docId ? prev[id].status : "pending" } }));
        }
    }, [clinicId, doctorId, refresh, docs]);

    const handleView = useCallback(async (id: string) => {
        const st = docs[id];
        if (st?.viewUrl) {
            window.open(st.viewUrl, "_blank", "noopener,noreferrer");
            return;
        }
        const doc = DOCS.find(d => d.id === id);
        if (!doc) return;
        try {
            let url: string | undefined;
            if (doc.source === "clinic") {
                if (!clinicId) return;
                url = await ComplianceAPI.getViewUrl(clinicId, doc.backendType!);
            } else {
                if (!doctorId || !st?.docId) return;
                url = (await DoctorKycAPI.viewUrl(doctorId, st.docId)).view_url;
            }
            if (url) window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Could not open the document. Please try again.");
        }
    }, [docs, clinicId, doctorId]);

    const submittedCount = DOCS.filter(d => docs[d.id].status !== "pending").length;

    return (
        <div className="space-y-5">

            {/* ── Clinic credentialing documents ─────────────────────── */}
            {/* id + scroll-mt let deep links (e.g. the dashboard "View KYC
                status" banner) scroll this card under the sticky header. */}
            <div id="clinic-credentialing" className="bg-white rounded-2xl p-6 scroll-mt-6 transition-shadow">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0">
                        <ShieldIcon className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Clinic credentialing documents</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Director / Owner Aadhaar and Owner PAN are required to prescribe; the rest are optional. Files must be clear, complete, and legible.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {DOCS.map(d => (
                        <DocTile
                            key={d.id}
                            doc={d}
                            state={docs[d.id]}
                            onFileSelect={handleFileSelect}
                            readOnly={!canEdit}
                            onView={handleView}
                        />
                    ))}
                </div>

                {/* Footer */}
                <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-xs text-slate-500">
                        {loading
                            ? "Loading documents…"
                            : `${submittedCount} of ${DOCS.length} documents submitted · Each upload is sent for SwasthiQ review automatically`}
                    </p>
                </div>
            </div>

            {/* ── Business & Registration Documents ──────────────────── */}
            <div id="business-docs" className="bg-white rounded-2xl p-6 scroll-mt-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center text-white flex-shrink-0">
                        <FileIcon className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Business &amp; registration documents</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Incorporation, GST, clinic registration and other regulatory docs. All optional — upload what applies to your clinic.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ONBOARDING_DOCS.map(d => (
                        <DocTile
                            key={d.id}
                            doc={d}
                            state={obDocs[d.id]}
                            onFileSelect={handleObFileSelect}
                            readOnly={!canEdit}
                            onView={handleObView}
                        />
                    ))}
                </div>

                <div className="mt-5 pt-4 border-t border-gray-100">
                    <p className="text-xs text-slate-500">
                        {obLoading
                            ? "Loading documents…"
                            : `${ONBOARDING_DOCS.filter(d => obDocs[d.id]?.status !== "pending").length} of ${ONBOARDING_DOCS.length} documents uploaded · Each upload is sent for SwasthiQ review automatically`}
                    </p>
                </div>
            </div>

        </div>
    );
};
